import React, { useCallback, useState } from 'react';
import { Avatar, Col, Row, Space, Tag, Typography } from 'antd';
import { Delivery } from '../../api/models/deliveries';
import Icon, {
  CalendarOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  NodeIndexOutlined,
  PhoneOutlined,
  ReadOutlined,
  RetweetOutlined,
  ShopOutlined,
  UserOutlined,
  WalletOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { colorBgBase, colorIcon } from '../../styles/theme';
import i18next from '../../i18next';
import Button from '../Button/Button';
import SmallBuildingIcon from '../../assets/img/smallBuilding.svg?react';
import styles from './CompletedDeliveryExpanded.module.less';
import RaiseEscalationModal from '../RaiseEscalationModal/RaiseEscalationModal';
import { raiseEscalationClicked } from '../../utils/analytika/events';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import classnames from 'classnames';
import { getPickupLocationTitle } from '../../utils/deliveries';
import { useSelector } from 'react-redux';
import { loadingSelector } from '../../redux/selectors/loading';
import { userSelector } from '../../redux/selectors/user';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import ImgIcon from '../../assets/img/imgIcon.svg?react';
import RiderBlackIcon from '../../assets/img/riderBlack.svg?react';
import ProofDeliveryModal from '../ProofDeliveryModal';
import StepsStatus from '../StepsStatus';
import DeliveryAttribute from '../DeliveryAttribute';
import SkeletonAvatar from 'antd/lib/skeleton/Avatar';
import { formatDateTime, isNumber } from '../../utils/componentHelpers';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import Banner from '../Banner/Banner';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { DeliveryOption } from '../../types';

type ExpandedRowProps = {
  delivery: Delivery;
  isRecreationInProgress: boolean;
  recreateDelivery: (bookingId: string, id: number | string) => Promise<void>;
};

const ExpandedTableRow = ({
  delivery: {
    cashOnDelivery,
    vehicle,
    id,
    bookingId,
    pickupLocation,
    dropOffLocation,
    driverDetails,
    driverNotes,
    passenger,
    completedAt,
    distance,
    price,
    status,
    priceCurrency,
    pickupAddressLine,
    proofImageUrl,
    cancellationReason,
    isCNF,
    bookingType
  },
  isRecreationInProgress = false,
  recreateDelivery
}: ExpandedRowProps) => {
  const recreateThisDelivery = () => recreateDelivery(bookingId, id);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const onEscalationModalClose = () => setShowEscalationModal(false);
  const [showProofDeliveryModal, setShowProofDeliveryModal] = useState(false);
  const onProofDeliveryModalClose = () => setShowProofDeliveryModal(false);
  const { fireEvent } = useAnalytika();
  const {
    effects: {
      deliveries: { fetchCompletedDeliveryDetails: isDetailsLoading }
    }
  } = useSelector(loadingSelector);
  const {
    userGalileoVars: { [FeatureFlag.IS_CONTACT_SUPPORT_ENABLED]: isContactSupportEnabled }
  } = useSelector(userSelector);

  const handleEscalationModalClick = useCallback(() => {
    fireEvent(raiseEscalationClicked({ delivery_id: id }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_RAISEESCALATION,
      event_version: 2,
      booking_id: bookingId,
      delivery_id: id
    });
    setShowEscalationModal(true);
  }, [setShowEscalationModal]);

  const { md: isMdBreakpoint } = useBreakpoint();

  const deliveryCost = isNumber(price) ? `${price} ${priceCurrency}` : i18next.t('pastDeliveriesPage.notAvailable');
  const deliveryDistance = distance || i18next.t('pastDeliveriesPage.notAvailable');
  const deliveryCompletion = completedAt ? formatDateTime(completedAt) : i18next.t('pastDeliveriesPage.notAvailable');

  return (
    <div className={styles.expandedCardWrapper}>
      <Row data-testid="completed-expanded-content-container">
        <Col md={12} xs={24} data-testid="completed-expanded-content-pickup">
          <Row className={classnames([styles.detailsRow])}>
            <Col className={classnames([styles.cardLeftCol, styles.stepsContainerWrapper])}>
              <StepsStatus status={status} />
            </Col>
            <Col>
              <Row>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={pickupLocation?.nickname || pickupLocation?.name}
                    attributeIcon={<ShopOutlined />}
                    isTitle
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={pickupAddressLine || (pickupLocation && getPickupLocationTitle(pickupLocation))}
                    attributeIcon={<EnvironmentOutlined />}
                  />
                </Col>
              </Row>
              <Row className={styles.nextDetailsBlock}>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={passenger ? `${passenger.firstName} ${passenger.lastName}` : ' - '}
                    attributeIcon={<FlagOutlined />}
                    isTitle
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className={classnames([styles.detailsRow])}>
            <Col className={styles.cardLeftCol} />
            <Col>
              <Row>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={dropOffLocation?.name || dropOffLocation?.area || dropOffLocation?.street || '-'}
                    attributeIcon={<EnvironmentOutlined />}
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={dropOffLocation?.building || '-'}
                    attributeIcon={<SmallBuildingIcon />}
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={driverNotes || '-'}
                    attributeIcon={<ReadOutlined />}
                    textType="secondary"
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={passenger?.phoneNumber || '-'}
                    attributeIcon={<PhoneOutlined />}
                    textType="secondary"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col md={12} xs={24} data-testid="completed-expanded-content-tripdetails">
          <Row className={classnames([styles.detailsRow, !isMdBreakpoint && styles.nextDetailsBlock])}>
            <Col className={styles.cardLeftCol}>
              {!isDetailsLoading ? (
                <Avatar size={40} icon={<UserOutlined />} src={driverDetails?.driverPicture} />
              ) : (
                <SkeletonAvatar active size={40} />
              )}
            </Col>
            <Col>
              <Row>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={
                      driverDetails
                        ? `${i18next.t('activeDeliveriesPage.yourDriver')}: ${driverDetails?.driverName}`
                        : i18next.t(`deliveries.deliveryStatus.${status}`)
                    }
                    isTitle
                  />
                </Col>
                {vehicle && (
                  <Col span={24}>
                    <DeliveryAttribute
                      attributeValue={
                        <Col style={{ display: 'flex', columnGap: '5px', flexWrap: 'wrap', rowGap: '5px' }}>
                          <Typography.Text>{`${vehicle?.vehicleModel}`}</Typography.Text>
                          {vehicle && (
                            <Tag style={{ background: colorBgBase }}>
                              <Typography.Text>{vehicle?.plateNumber}</Typography.Text>
                            </Tag>
                          )}
                        </Col>
                      }
                      attributeIcon={<RiderBlackIcon />}
                    />
                  </Col>
                )}
                {driverDetails && (
                  <Col span={24}>
                    <DeliveryAttribute
                      attributeValue={driverDetails?.driverNumber || '-'}
                      attributeIcon={<PhoneOutlined />}
                      textType="secondary"
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row className={classnames([styles.nextDetailsBlock, styles.detailsRow])}>
            <Col className={styles.cardLeftCol} />
            <Col>
              <Row>
                <Col span={24}>
                  <DeliveryAttribute attributeValue={i18next.t('pastDeliveriesPage.tripDetails')} isTitle />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={`${i18next.t('pastDeliveriesPage.costOfDelivery')}: ${deliveryCost}`}
                    attributeIcon={<WalletOutlined />}
                    textType="secondary"
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={`${i18next.t('activeDeliveriesPage.cashToBeCollected')}: ${cashOnDelivery || '-'}`}
                    attributeIcon={<DollarOutlined />}
                    textType="secondary"
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={`${i18next.t('pastDeliveriesPage.distance')}: ${deliveryDistance}`}
                    attributeIcon={<NodeIndexOutlined />}
                    textType="secondary"
                  />
                </Col>
                <Col span={24}>
                  <DeliveryAttribute
                    attributeValue={`${i18next.t('pastDeliveriesPage.completionDateTime')}: ${deliveryCompletion}`}
                    attributeIcon={<CalendarOutlined />}
                    textType="secondary"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      {proofImageUrl && (
        <Row className={styles.bannerRow} gutter={[20, 20]}>
          <Col md={12} xs={24}>
            <div className={styles.deliveryProofContainer}>
              <Space>
                <Icon color={colorIcon} size={14} component={() => <ImgIcon />} />
                <Typography.Text>{i18next.t('pastDeliveriesPage.deliveryProof')}</Typography.Text>
              </Space>
              <Button onClick={() => setShowProofDeliveryModal(true)}>
                {i18next.t('pastDeliveriesPage.previewButton')}
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {cancellationReason && (
        <Row className={styles.bannerRow} gutter={[20, 20]}>
          <Col md={12} xs={24}>
            <Banner
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${i18next.t('pastDeliveriesPage.orderCancelled')} : ${i18next.t(
                `cancellationReasons.${cancellationReason}`
              )}`}
            />
          </Col>
        </Row>
      )}

      {isCNF && (
        <Row className={styles.bannerRow} gutter={[20, 20]}>
          <Col md={12} xs={24}>
            <Banner
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${i18next.t('pastDeliveriesPage.captainNotFound')}`}
            />
          </Col>
        </Row>
      )}

      <Row gutter={[20, 20]} className={styles.cancelButtonRow}>
        {isContactSupportEnabled && (
          <Col style={isMdBreakpoint ? { flex: 'none' } : undefined} sm={12} xs={24}>
            <Button
              block={!isMdBreakpoint}
              size={'large'}
              onClick={handleEscalationModalClick}
              icon={<CustomerServiceOutlined />}
              type={'default'}
            >
              {i18next.t('pastDeliveriesPage.contactSupport')}
            </Button>
          </Col>
        )}
        <Col sm={12} xs={24}>
          <Button
            block={!isMdBreakpoint}
            size={'large'}
            onClick={recreateThisDelivery}
            loading={isRecreationInProgress}
            disabled={isRecreationInProgress || bookingType === DeliveryOption.SCHEDULED}
            icon={<RetweetOutlined />}
            type={'primary'}
          >
            {i18next.t('pastDeliveriesPage.recreateTitle')}
          </Button>
        </Col>
      </Row>
      {showProofDeliveryModal && proofImageUrl && (
        <ProofDeliveryModal
          imgUrl={proofImageUrl}
          visible={showProofDeliveryModal}
          onClose={onProofDeliveryModalClose}
        />
      )}
      {showEscalationModal && (
        <RaiseEscalationModal referenceId={bookingId} visible={showEscalationModal} onClose={onEscalationModalClose} />
      )}
    </div>
  );
};

export default ExpandedTableRow;
